import React, { useState } from 'react';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { setToken, setUser } from '../utils/auth';
import { getCurrentSchool, ASSETS } from '../config/schools';
import { FaBolt, FaNewspaper, FaShieldAlt, FaTrophy } from 'react-icons/fa';

function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const school = getCurrentSchool();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/signup';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;
      
      let response;
      
      if (isLogin) {
        // Try normal login first
        try {
          response = await api.post('/auth/login', payload);
        } catch (normalLoginError) {
          // If normal login fails, try educator demo login
          try {
            response = await api.post('/educator-demo/login', payload);
          } catch (educatorLoginError) {
            // If educator login fails, try concours login
            try {
              response = await api.post('/concours/login', payload);
            } catch (concoursLoginError) {
              // All failed, throw the original error
              throw normalLoginError;
            }
          }
        }
      } else {
        response = await api.post(endpoint, payload);
      }
      
      setToken(response.data.token);
      setUser(response.data.user);
      
      // Redirect based on role
      if (response.data.user.role === 'SUPER_ADMIN') {
        window.location.href = '/superadmin';
      } else if (response.data.user.role === 'ADMIN') {
        window.location.href = '/admin';
      } else if (response.data.user.role === 'CONCOURS_STUDENT') {
        window.location.href = '/concours-dashboard';
      } else {
        window.location.href = '/dashboard';
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #FFB703 0%, #FB8500 50%, #E63946 100%)' }}>
      {/* Comic dots pattern overlay */}
      <div className="absolute inset-0 halftone-bg opacity-10"></div>
      
      {/* Animated stars */}
      <motion.div
        className="absolute top-20 left-10 text-6xl"
        animate={{ rotate: [0, 360], scale: [1, 1.2, 1] }}
        transition={{ duration: 3, repeat: Infinity }}
      >
        ⭐
      </motion.div>
      <motion.div
        className="absolute bottom-20 right-10 text-5xl"
        animate={{ rotate: [360, 0], scale: [1, 1.3, 1] }}
        transition={{ duration: 2.5, repeat: Infinity }}
      >
        💥
      </motion.div>

      <div className="relative z-10 container mx-auto px-4 py-12">
        {/* Logo - école si défini, sinon DailyGlobe */}
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          {school.showSchoolLogo && school.logo ? (
            <img 
              src={school.logo}
              alt={school.name}
              className="w-48 h-48 mx-auto mb-4 object-contain drop-shadow-2xl bg-white rounded-lg p-4"
            />
          ) : (
            <img 
              src={ASSETS.DAILYGLOBE_LOGO}
              alt="DailyGlobe School"
              className="w-48 h-48 mx-auto mb-4 object-contain drop-shadow-2xl"
            />
          )}
          <h1 className="text-4xl md:text-5xl comic-title" style={{ color: '#1B4965', textShadow: '4px 4px 0px #FFB703, 8px 8px 0px rgba(0,0,0,0.2)' }}>
            {school.showSchoolLogo ? school.name : 'DailyGlobe School'}
          </h1>
          <p className="text-lg md:text-xl comic-text mt-2" style={{ color: '#1B4965' }}>
            Connexion à la plateforme DailyGlobe
          </p>
        </motion.div>

        {/* Auth Form */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="max-w-md mx-auto comic-panel bg-white"
        >
          <div className="flex justify-center space-x-4 mb-6">
            <button
              onClick={() => setIsLogin(true)}
              className={`comic-button ${isLogin ? 'comic-button-navy' : 'bg-gray-200'}`}
              data-testid="login-tab-button"
            >
              Se connecter
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`comic-button ${!isLogin ? 'comic-button-navy' : 'bg-gray-200'}`}
              data-testid="signup-tab-button"
            >
              S'inscrire
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
            <div>
              <label className="block text-sm font-bold mb-2" style={{ fontFamily: 'Bebas Neue' }}>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full input-comic"
                required
                data-testid="email-input"
                placeholder={`reporter@${school.id}.fr`}
              />
            </div>

            <div>
              <label className="block text-sm font-bold mb-2" style={{ fontFamily: 'Bebas Neue' }}>Mot de passe</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full input-comic"
                required
                data-testid="password-input"
                placeholder="••••••••"
              />
            </div>

            {!isLogin && (
              <>
                <div>
                  <label className="block text-sm font-bold mb-2" style={{ fontFamily: 'Bebas Neue' }}>Prénom</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full input-comic"
                    data-testid="firstname-input"
                    placeholder="Clark"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-2" style={{ fontFamily: 'Bebas Neue' }}>Nom</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full input-comic"
                    data-testid="lastname-input"
                    placeholder="Kent"
                  />
                </div>
              </>
            )}

            {error && (
              <motion.div
                initial={{ x: -10 }}
                animate={{ x: 0 }}
                className="speech-bubble bg-red-100 border-red-500"
                data-testid="error-message"
              >
                <p className="text-red-700 font-bold">{error}</p>
              </motion.div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full comic-button comic-button-red text-xl py-3"
              data-testid="submit-button"
            >
              {loading ? 'CHARGEMENT...' : (isLogin ? 'CONNEXION' : 'INSCRIPTION')}
            </button>
          </form>

          {!isLogin && (
            <div className="mt-4 p-3 bg-yellow-50 border-3 border-yellow-500 rounded">
              <p className="text-sm"><strong>Note:</strong> Seuls les emails autorisés par l'administrateur peuvent s'inscrire.</p>
            </div>
          )}
        </motion.div>

        {/* Features */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto"
        >
          <div className="comic-panel text-center">
            <FaNewspaper className="text-5xl mx-auto mb-3" style={{ color: '#1B4965' }} />
            <h3 className="comic-text text-xl mb-2">Quiz & Entraînements</h3>
            <p className="text-sm">Développe tes super pouvoirs!</p>
          </div>
          <div className="comic-panel text-center">
            <FaBolt className="text-5xl mx-auto mb-3" style={{ color: '#E63946' }} />
            <h3 className="comic-text text-xl mb-2">Créé par des Pros</h3>
            <p className="text-sm">Des journalistes et professeurs expérimentés</p>
          </div>
          <div className="comic-panel text-center">
            <FaShieldAlt className="text-5xl mx-auto mb-3" style={{ color: '#FFB703' }} />
            <h3 className="comic-text text-xl mb-2">Pédagogie Ludique</h3>
            <p className="text-sm">Apprendre en s'amusant, c'est possible!</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default AuthPage;
